/** The Run Kinematics report tab */
import { IonCol, IonGrid, IonRow, IonText, IonCardContent, IonFabButton, IonIcon, IonCardTitle } from '@ionic/react';
import { colorPalette, helpCircleOutline } from 'ionicons/icons';
import { useEffect, useMemo, useState } from 'react';

import { FunctionalMovementKinematicsGraph } from '@/pages/ReportV2/components/Graphs';
import styles from '@/pages/ReportV2/Styles/Reports.module.css';

import { CardHeader } from '@/components/CardHeader/cardHeader';
import { KinematicsCardHeader } from '@/components/CardHeader/KinematicsCardHeader';
import { Card } from '@/components/Cards';
import { ChartLegend } from '@/components/ChartLegend/ChartLegend';
import { DataTable } from '@/components/DataTable/DataTable';
import { DataTableColumn } from '@/components/DataTable/DataTableColumn';
import { DataTableHeader } from '@/components/DataTable/DataTableHeader';
import { DataTableRow } from '@/components/DataTable/DataTableRow';
import { DataTableSection } from '@/components/DataTable/DataTableSection';
import { Icon } from '@/components/Icon';
import { toast } from '@/components/Toaster/Toast';

import { mergeClassNames } from '@/utils/mergeClassNames';

import { useAuthContext } from '@/contexts/authContext';

import toggle from '@/assets/icons/toggle-left.svg';

import { SquatKinematicData } from '@/models/reportV2.model';

type KinematicsReportProps = {
  tabData: SquatKinematicData;
  setFloatingButtons: (buttons: React.ReactNode) => void;
};

/** Run kinematics report tab
 * @param report - the full run report info and data
 */
export const SquatsKinematicsReportTab = ({ tabData, setFloatingButtons }: KinematicsReportProps) => {
  const [showAllStrides, setShowAllStrides] = useState<boolean>(false);
  const userRole = useAuthContext().state.userState?.role;

  const toggleShowAllStrides = () => {
    setShowAllStrides((prev) => {
      const newValue = !prev;
      localStorage.setItem('showAllStridesKinematics', newValue ? 'true' : 'false');
      return newValue;
    });
  };

  const colors = ['tableLabel_succes', 'tableLabel_warning', 'tableLabel_danger', 'tableLabel_succes'];
  function getColor(index: number) {
    return colors[index % colors.length];
  }

  const seriesPelvis = useMemo(() => {
    return showAllStrides ? tabData.pelvisKinematicData.allStrides : tabData.pelvisKinematicData.mean;
  }, [showAllStrides, tabData]);
  const seriesHip = useMemo(() => {
    return showAllStrides ? tabData.hipKinematicData.allStrides : tabData.hipKinematicData.mean;
  }, [showAllStrides, tabData]);
  const seriesThigh = useMemo(() => {
    return showAllStrides ? tabData.thighKinematicData.allStrides : tabData.thighKinematicData.mean;
  }, [showAllStrides, tabData]);
  const seriesKnee = useMemo(() => {
    return showAllStrides ? tabData.kneeKinematicData.allStrides : tabData.kneeKinematicData.mean;
  }, [showAllStrides, tabData]);
  const seriesCalf = useMemo(() => {
    return showAllStrides ? tabData.calfKinematicData.allStrides : tabData.calfKinematicData.mean;
  }, [showAllStrides, tabData]);
  const seriesAnkle = useMemo(() => {
    return showAllStrides ? tabData.ankleKinematicData.allStrides : tabData.ankleKinematicData.mean;
  }, [showAllStrides, tabData]);
  const seriesFoot = useMemo(() => {
    return showAllStrides ? tabData.footKinematicData.allStrides : tabData.footKinematicData.mean;
  }, [showAllStrides, tabData]);

  const showLegendToast = () => {
    toast({
      children: <ChartLegend />,
    });
  };
  const showInfoToast = () =>
    toast({
      icon: 'info',
      title: 'Enlarge graphs',
      description: 'Do this by clicking / tapping on the chart',
      duration: 3000,
    });

  useEffect(() => {
    const buttons = (
      <>
        <IonFabButton onClick={() => toggleShowAllStrides()} title={'Toggle Mean / All Movements'}>
          <IonIcon icon={toggle}></IonIcon>
        </IonFabButton>
        <IonFabButton onClick={() => showLegendToast()} title={'Show Graph Legend'}>
          <IonIcon icon={colorPalette}></IonIcon>
        </IonFabButton>
        <IonFabButton onClick={() => showInfoToast()} title={'Show Info'}>
          <Icon name={'info'}></Icon>
        </IonFabButton>
      </>
    );
    setFloatingButtons(buttons); // Update buttons in parent
    return () => setFloatingButtons(null); // Clean up on unmount
  }, [setFloatingButtons]);

  if (!tabData) {
    return <IonText>Data not found.</IonText>;
  }
  return (
    <>
      <IonRow className='ion-no-padding'>
        <IonCol className='' size='12'>
          <Card style={{ height: 'fit-content', marginLeft: 0, marginRight: 0, marginTop: 0 }}>
            <KinematicsCardHeader />
            <IonCardContent>
              <IonRow style={{ borderBottom: '1px solid #ddd', marginBottom: '6px', backgroundColor: 'transparent' }}>
                <IonGrid className='ion-no-padding'>
                  <IonRow className=''>
                    <IonCardTitle className={styles.title}>Pelvis</IonCardTitle>
                  </IonRow>
                  <IonRow className='ion-no-padding'>
                    <IonCol className='ion-no-padding' size='4'>
                      <FunctionalMovementKinematicsGraph
                        data={seriesPelvis.Sagittal.series}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'Anterior tilt (-) / Posterior tilt (+)'}
                        title={'Sagittal'}
                        yAxis={seriesPelvis.Sagittal.yAxis}
                      />
                    </IonCol>
                    <IonCol className='ion-no-padding' size='4'>
                      <FunctionalMovementKinematicsGraph
                        data={seriesPelvis.Coronal.series}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'Drop (-) / Hike (+)'}
                        title={'Frontal'}
                        yAxis={seriesPelvis.Coronal.yAxis}
                      />
                    </IonCol>
                    <IonCol className='ion-no-padding' size='4'>
                      <FunctionalMovementKinematicsGraph
                        data={seriesPelvis.Transversal.series}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'Backward Rotation (-) / Forward Rotation (+)'}
                        title={'Transversal'}
                        yAxis={seriesPelvis.Transversal.yAxis}
                      />
                    </IonCol>
                  </IonRow>
                </IonGrid>
              </IonRow>

              <IonRow style={{ borderBottom: '1px solid #ddd', marginBottom: '6px' }}>
                <IonGrid className='ion-no-padding'>
                  <IonRow className=''>
                    <IonCardTitle className={styles.title}>Hip</IonCardTitle>
                  </IonRow>
                  <IonRow className='ion-no-padding'>
                    <IonCol className='ion-no-padding' size='4'>
                      <FunctionalMovementKinematicsGraph
                        data={seriesHip.Sagittal.series}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'Extension (-) / Flexion (+)'}
                        title={'Sagittal'}
                        yAxis={seriesHip.Sagittal.yAxis}
                      />
                    </IonCol>
                    <IonCol className='ion-no-padding' size='4'>
                      <FunctionalMovementKinematicsGraph
                        data={seriesHip.Coronal.series}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'Abduction (-) / Adduction (+)'}
                        title={'Frontal'}
                        yAxis={seriesHip.Coronal.yAxis}
                      />
                    </IonCol>
                    <IonCol className='ion-no-padding' size='4'>
                      <FunctionalMovementKinematicsGraph
                        data={seriesHip.Transversal.series}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'External Rotation (-) / Internal Rotation (+)'}
                        title={'Transversal'}
                        yAxis={seriesHip.Transversal.yAxis}
                      />
                    </IonCol>
                  </IonRow>
                </IonGrid>
              </IonRow>

              {userRole === 'ORYX_Admin' && (
                <IonRow style={{ borderBottom: '1px solid #ddd', marginBottom: '6px' }}>
                  <IonGrid className='ion-no-padding'>
                    <IonRow className=''>
                      <IonCardTitle className={styles.title}>Thigh</IonCardTitle>
                    </IonRow>
                    <IonRow className='ion-no-padding'>
                      <IonCol className='ion-no-padding' size='4'>
                        <FunctionalMovementKinematicsGraph
                          data={seriesThigh.Sagittal.series}
                          strokeWidth={showAllStrides ? 1 : 2}
                          subtitle={'Extension (-) / Flexion (+)'}
                          title={'Sagittal'}
                          yAxis={seriesThigh.Sagittal.yAxis}
                        />
                      </IonCol>
                      <IonCol className='ion-no-padding' size='4'>
                        <FunctionalMovementKinematicsGraph
                          data={seriesThigh.Coronal.series}
                          strokeWidth={showAllStrides ? 1 : 2}
                          subtitle={'Abduction (-) / Adduction (+)'}
                          title={'Frontal'}
                          yAxis={seriesThigh.Coronal.yAxis}
                        />
                      </IonCol>
                      <IonCol className='ion-no-padding' size='4'>
                        <FunctionalMovementKinematicsGraph
                          data={seriesThigh.Transversal.series}
                          strokeWidth={showAllStrides ? 1 : 2}
                          subtitle={'External Rotation (-) / Internal Rotation (+)'}
                          title={'Transversal'}
                          yAxis={seriesThigh.Transversal.yAxis}
                        />
                      </IonCol>
                    </IonRow>
                  </IonGrid>
                </IonRow>
              )}

              <IonRow style={{ borderBottom: '1px solid #ddd', marginBottom: '6px' }}>
                <IonGrid className='ion-no-padding'>
                  <IonRow className=''>
                    <IonCardTitle className={styles.title}>Knee</IonCardTitle>
                  </IonRow>
                  <IonRow className='ion-no-padding'>
                    <IonCol className='ion-no-padding' size='4'>
                      <FunctionalMovementKinematicsGraph
                        data={seriesKnee.Sagittal.series}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'Extension (-) / Flexion (+)'}
                        title={'Sagittal'}
                        yAxis={seriesKnee.Sagittal.yAxis}
                      />
                    </IonCol>
                    <IonCol className='ion-no-padding' size='4'>
                      <FunctionalMovementKinematicsGraph
                        data={seriesKnee.Coronal.series}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'Valgus (-) / Varus (+)'}
                        title={'Frontal'}
                        yAxis={seriesKnee.Coronal.yAxis}
                      />
                    </IonCol>
                    <IonCol className='ion-no-padding' size='4'>
                      <FunctionalMovementKinematicsGraph
                        data={seriesKnee.Transversal.series}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'External Rotation (-) / Internal Rotation (+)'}
                        title={'Transversal'}
                        yAxis={seriesKnee.Transversal.yAxis}
                      />
                    </IonCol>
                  </IonRow>
                </IonGrid>
              </IonRow>

              {userRole === 'ORYX_Admin' && (
                <IonRow style={{ borderBottom: '1px solid #ddd', marginBottom: '6px' }}>
                  <IonGrid className='ion-no-padding'>
                    <IonRow className=''>
                      <IonCardTitle className={styles.title}>Calf</IonCardTitle>
                    </IonRow>
                    <IonRow className='ion-no-padding'>
                      <IonCol className='ion-no-padding' size='4'>
                        <FunctionalMovementKinematicsGraph
                          data={seriesCalf.Sagittal.series}
                          strokeWidth={showAllStrides ? 1 : 2}
                          subtitle={'Extension (-) / Flexion (+)'}
                          title={'Sagittal'}
                          yAxis={seriesCalf.Sagittal.yAxis}
                        />
                      </IonCol>
                      <IonCol className='ion-no-padding' size='4'>
                        <FunctionalMovementKinematicsGraph
                          data={seriesCalf.Coronal.series}
                          strokeWidth={showAllStrides ? 1 : 2}
                          subtitle={'Abduction (-) / Adduction (+)'}
                          title={'Frontal'}
                          yAxis={seriesCalf.Coronal.yAxis}
                        />
                      </IonCol>
                      <IonCol className='ion-no-padding' size='4'>
                        <FunctionalMovementKinematicsGraph
                          data={seriesCalf.Transversal.series}
                          strokeWidth={showAllStrides ? 1 : 2}
                          subtitle={'External Rotation (-) / Internal Rotation (+)'}
                          title={'Transversal'}
                          yAxis={seriesCalf.Transversal.yAxis}
                        />
                      </IonCol>
                    </IonRow>
                  </IonGrid>
                </IonRow>
              )}

              <IonRow>
                <IonGrid className='ion-no-padding'>
                  <IonRow className=''>
                    <IonCardTitle className={styles.title}>Ankle</IonCardTitle>
                  </IonRow>
                  <IonRow>
                    <IonCol className='ion-no-padding' size='4'>
                      <FunctionalMovementKinematicsGraph
                        data={seriesAnkle.Sagittal.series}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'Plantarflexion (-) / Dorsalflexion (+)'}
                        title={'Sagittal'}
                        yAxis={seriesAnkle.Sagittal.yAxis}
                      />
                    </IonCol>
                    <IonCol className='ion-no-padding' size='4'>
                      <FunctionalMovementKinematicsGraph
                        data={seriesAnkle.Coronal.series}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'Eversion (-) / Inversion (+)'}
                        title={'Frontal'}
                        yAxis={seriesAnkle.Coronal.yAxis}
                      />
                    </IonCol>
                    <IonCol className='ion-no-padding' size='4'>
                      <FunctionalMovementKinematicsGraph
                        data={seriesAnkle.Transversal.series}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'External Rotation (-) / Internal Rotation (+)'}
                        title={'Transversal'}
                        yAxis={seriesAnkle.Transversal.yAxis}
                      />
                    </IonCol>
                  </IonRow>
                </IonGrid>
              </IonRow>

              {userRole === 'ORYX_Admin' && (
                <IonRow>
                  <IonGrid className='ion-no-padding'>
                    <IonRow className=''>
                      <IonCardTitle className={styles.title}>Foot</IonCardTitle>
                    </IonRow>
                    <IonRow>
                      <IonCol className='ion-no-padding' size='4'>
                        <FunctionalMovementKinematicsGraph
                          data={seriesFoot.Sagittal.series}
                          strokeWidth={showAllStrides ? 1 : 2}
                          subtitle={'Plantarflexion (-) / Dorsalflexion (+)'}
                          title={'Sagittal'}
                          yAxis={seriesFoot.Sagittal.yAxis}
                        />
                      </IonCol>
                      <IonCol className='ion-no-padding' size='4'>
                        <FunctionalMovementKinematicsGraph
                          data={seriesFoot.Coronal.series}
                          strokeWidth={showAllStrides ? 1 : 2}
                          subtitle={'Eversion (-) / Inversion (+)'}
                          title={'Frontal'}
                          yAxis={seriesFoot.Coronal.yAxis}
                        />
                      </IonCol>
                      <IonCol className='ion-no-padding' size='4'>
                        <FunctionalMovementKinematicsGraph
                          data={seriesFoot.Transversal.series}
                          strokeWidth={showAllStrides ? 1 : 2}
                          subtitle={'External Rotation (-) / Internal Rotation (+)'}
                          title={'Transversal'}
                          yAxis={seriesFoot.Transversal.yAxis}
                        />
                      </IonCol>
                    </IonRow>
                  </IonGrid>
                </IonRow>
              )}
            </IonCardContent>
          </Card>
        </IonCol>
      </IonRow>

      {/* Tables */}
      <IonRow class='ion-no-padding ion-justify-content-center ion-padding-bottom'>
        <IonCol size='12'>
          <Card style={{ margin: 0 }}>
            <CardHeader title={'Range of Motion'} />
            <IonCardContent>
              <IonGrid className='ion-no-padding'>
                <IonRow
                  className={mergeClassNames(styles.full_height, 'ion-padding-vertical', 'ion-align-items-start')}>
                  <DataTable>
                    <DataTableHeader titles={['', 'Left', 'Right', 'Symmetry']} />

                    <DataTableSection>
                      <DataTableRow>
                        <DataTableColumn type='key'>Pelvis</DataTableColumn>
                        <DataTableColumn type='value'>
                          {tabData.romData[0][0].toFixed(1) + '°'} ± {tabData.romData[0][1].toFixed(1) + '°'}
                        </DataTableColumn>
                        <DataTableColumn type='value'>
                          {tabData.romData[0][2].toFixed(1) + '°'} ± {tabData.romData[0][3].toFixed(1) + '°'}
                        </DataTableColumn>
                        <DataTableColumn type='value'>{tabData.romData[0][4].toFixed(0) + '%'}</DataTableColumn>
                      </DataTableRow>
                    </DataTableSection>
                    <DataTableSection>
                      <DataTableRow>
                        <DataTableColumn type='key'>Hip</DataTableColumn>
                        <DataTableColumn type='value'>
                          {tabData.romData[1][0].toFixed(1) + '°'} ± {tabData.romData[1][1].toFixed(1) + '°'}
                        </DataTableColumn>
                        <DataTableColumn type='value'>
                          {tabData.romData[1][2].toFixed(1) + '°'} ± {tabData.romData[1][3].toFixed(1) + '°'}
                        </DataTableColumn>
                        <DataTableColumn type='value'>{tabData.romData[1][4].toFixed(0) + '%'}</DataTableColumn>
                      </DataTableRow>
                      <DataTableRow>
                        <DataTableColumn indent type='key'>
                          External rotation <IonIcon id='trigger-hip-external-rotation' src={helpCircleOutline} />
                        </DataTableColumn>
                        <DataTableColumn label={getColor(tabData.qualityData[2][0])} type='value'>
                          {tabData.hipLabels.externalRotationLabel_Left}
                        </DataTableColumn>
                        <DataTableColumn label={getColor(tabData.qualityData[2][1])} type='value'>
                          {tabData.hipLabels.externalRotationLabel_Right}
                        </DataTableColumn>
                        <DataTableColumn />
                      </DataTableRow>
                      <DataTableRow>
                        <DataTableColumn indent type='key'>
                          Internal rotation <IonIcon id='trigger-hip-internal-rotation' src={helpCircleOutline} />
                        </DataTableColumn>
                        <DataTableColumn label={getColor(tabData.qualityData[3][0])} type='value'>
                          {tabData.hipLabels.internalRotationLabel_Left}
                        </DataTableColumn>
                        <DataTableColumn label={getColor(tabData.qualityData[3][1])} type='value'>
                          {tabData.hipLabels.internalRotationLabel_Right}
                        </DataTableColumn>
                        <DataTableColumn />
                      </DataTableRow>
                    </DataTableSection>

                    <DataTableSection>
                      <DataTableRow>
                        <DataTableColumn type='key'>Knee</DataTableColumn>
                        <DataTableColumn type='value'>
                          {tabData.romData[2][0].toFixed(1) + '°'} ± {tabData.romData[2][1].toFixed(1) + '°'}
                        </DataTableColumn>
                        <DataTableColumn type='value'>
                          {tabData.romData[2][2].toFixed(1) + '°'} ± {tabData.romData[2][3].toFixed(1) + '°'}
                        </DataTableColumn>
                        <DataTableColumn type='value'>{tabData.romData[2][4].toFixed(0) + '%'}</DataTableColumn>
                      </DataTableRow>
                      <DataTableRow>
                        <DataTableColumn indent type='key'>
                          Varus / Valgus <IonIcon id='trigger-knee-valgus' src={helpCircleOutline} />
                        </DataTableColumn>
                        <DataTableColumn label={getColor(tabData.qualityData[4][0])} type='value'>
                          {tabData.varusValgusLabel_Left}
                        </DataTableColumn>
                        <DataTableColumn label={getColor(tabData.qualityData[4][1])} type='value'>
                          {tabData.varusValgusLabel_Right}
                        </DataTableColumn>
                        <DataTableColumn />
                      </DataTableRow>
                    </DataTableSection>

                    <DataTableSection>
                      <DataTableRow>
                        <DataTableColumn type='key'>Ankle</DataTableColumn>
                        <DataTableColumn type='value'>
                          {tabData.romData[3][0].toFixed(1) + '°'} ± {tabData.romData[3][1].toFixed(1) + '°'}
                        </DataTableColumn>
                        <DataTableColumn type='value'>
                          {tabData.romData[3][2].toFixed(1) + '°'} ± {tabData.romData[3][3].toFixed(1) + '°'}
                        </DataTableColumn>
                        <DataTableColumn type='value'>{tabData.romData[3][4].toFixed(0) + '%'}</DataTableColumn>
                      </DataTableRow>
                      <DataTableRow>
                        <DataTableColumn indent type='key'>
                          Early heel raise
                        </DataTableColumn>
                        <DataTableColumn label={getColor(tabData.qualityData[0][0])} type='value'>
                          {tabData.ankleLabels.earlyHeelRaiseLabel_Left}
                        </DataTableColumn>
                        <DataTableColumn label={getColor(tabData.qualityData[0][1])} type='value'>
                          {tabData.ankleLabels.earlyHeelRaiseLabel_Right}
                        </DataTableColumn>
                        <DataTableColumn />
                      </DataTableRow>
                    </DataTableSection>
                  </DataTable>
                </IonRow>
              </IonGrid>
            </IonCardContent>
          </Card>
        </IonCol>
      </IonRow>
    </>
  );
};
